import React, {useEffect, useRef, useState} from 'react';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Alignment,
  AutoImage,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlEmbed,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  MediaEmbedToolbar,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
// CKEditor5 NIM 다국어 번역 파일
import koTranslations from 'ckeditor5/translations/ko.js';
import enTranslations from 'ckeditor5/translations/en.js';

// 커스텀 플러그인
import ListIndentFixer from './plugins/ListIndentFixer';

import styled from "styled-components";
import {EditorStyle} from "style/SsqEditorStyle";
import {useIsMobile} from "hooks/useIsMobile";
import './ssqeditor.css';
import clsx from 'clsx';

interface Editor5Props {
  contents: string;
  onChangeData: Function;
  isDisabled?: boolean;
  width: string;
  height: string;
  lang: string;
  border?: boolean;
}

const trans = {
  ko: {
    'Subtitle': '부제목'
  }, en: {
    'Subtitle': 'Subtitle'
  }
};

const t = (lang, key) => {
  return trans?.[lang]?.[key] || key;
};

// 기존 SsqEditor5.tsx에서 실제 사용하던 툴바 설정
const pcToolbar = {
  items: [
    "heading",
    "|",
    "bold",
    "italic",
    "underline",
    "removeFormat",
    "findAndReplace",
    "fontColor",
    "fontSize",
    "|",
    "highlight:yellowMarker",
    "removeHighlight",
    "|",
    "alignment:left",
    "alignment:center",
    "alignment:right",
    "|",
    "numberedList",
    "bulletedList",
    "indent",
    "outdent",
    "blockQuote",
    "horizontalLine",
    "specialCharacters",
    "|",
    "imageInsert",
    "insertTable",
    "mediaEmbed",
    "link",
    "|",
    "codeBlock",
    "sourceEditing",
    "findAndReplace",
  ],
  shouldNotGroupWhenFull: true,
};

const mobileToolbar = {items: ["undo", "redo", "|", ...pcToolbar.items], shouldNotGroupWhenFull: false};

// CKEditor5 NIM plugins list
const editorPlugins = [
  Alignment,
  AutoImage,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlEmbed,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  MediaEmbedToolbar,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  // 커스텀 플러그인
  ListIndentFixer,
];

// HTML 구조 변환 함수 (에디터에서 테이블 구조를 유지하기 위해 사용)
const sanitizeTableStructure = (htmlContent: string) => {
  if (!htmlContent) return '';

  try {
    const parser = new DOMParser();
    // HTML 파싱 (text/html로 파싱해야 테이블 구조가 유지됨)
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const tables = doc.querySelectorAll('table');

    tables.forEach(table => {
      const thead = table.querySelector('thead');
      const tbody = table.querySelector('tbody');

      if (thead) {
        // 1. thead가 있으면 처리 시작
        const headerRows = Array.from(thead.querySelectorAll('tr'));

        // 2. thead의 모든 행을 tbody의 맨 앞으로 이동
        if (!tbody) {
          // tbody가 없으면 새로 생성
          const newTbody = doc.createElement('tbody');
          headerRows.forEach(row => newTbody.appendChild(row));
          table.appendChild(newTbody);
        } else {
          // tbody가 있으면 맨 앞에 삽입 (역순으로 넣어야 순서 유지)
          for (let i = headerRows.length - 1; i >= 0; i--) {
            if (tbody.firstChild) {
              tbody.insertBefore(headerRows[i], tbody.firstChild);
            } else {
              tbody.appendChild(headerRows[i]);
            }
          }
        }
        // 3. 빈 thead 제거
        thead.remove();
      }

      // 4. 모든 th를 td로 변환하고 클래스 추가
      const ths = table.querySelectorAll('th');
      ths.forEach(th => {
        const td = doc.createElement('td');
        // 기존 속성 복사
        Array.from(th.attributes).forEach(attr => {
          td.setAttribute(attr.name, attr.value);
        });
        // 기존 자식 노드(텍스트, 태그 등) 이동
        while (th.firstChild) {
          td.appendChild(th.firstChild);
        }
        // [중요] 헤더 스타일을 위한 클래스 추가
        td.classList.add('custom-header'); // 나중에 CSS에서 이 클래스에 th 스타일 적용

        // 교체
        th.parentNode?.replaceChild(td, th);
      });
    });

    return doc.body.innerHTML;
  } catch (e) {
    console.error('Table sanitization failed:', e);
    return htmlContent; // 실패하면 원본 반환
  }
};

function SsqEditor5(props: Editor5Props) {

  const [key, setKey] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    setKey(k => k + 1);
  }, [isMobile]);

  // 초기 렌더링 시에만 변환을 수행하고, 이후 props.contents가 변경되더라도
  // 에디터 내부 상태를 유지하기 위해 재계산하지 않음 (의존성 배열 비움)
  const [safeContents] = useState(() => sanitizeTableStructure(props.contents));

  // 마지막으로 에디터가 내보낸 내용을 추적 — 외부에서 contents가 바뀐 경우(템플릿 적용 등)를 감지하기 위함
  const lastSentContent = useRef(props.contents);
  const editorInstance = useRef<any>(null);

  useEffect(() => {
    if (props.contents !== lastSentContent.current && editorInstance.current) {
      editorInstance.current.setData(sanitizeTableStructure(props.contents));
      lastSentContent.current = props.contents;
    }
  }, [props.contents]);

  const language = (props.lang || "ko").split("-")[0] || 'ko';

  // translations config에 번역 데이터 추가 필요
  const translations = language === 'ko' ? [koTranslations] : [enTranslations];

  return (
    <React.Suspense fallback={<div></div>}>
      <Container $editorHeight={props.height}
        style={{height: 'calc( ' + props.height + ' + 40px )', width: props.width}} key={key} className="ssqeditor-cont">
        {/*ck-content 클래스는 에디터에서 자동으로 붙임. */}
        <EditorWrap className={clsx("SsqEditor", {'border': props.border})}
          $editorHeight={isMobile ? 'calc( ' + props.height + ' + 40px )' : props.height}>
          <CKEditor
            editor={ClassicEditor}
            disabled={props.isDisabled}
            config={{
              plugins: editorPlugins,
              language,
              translations,
              simpleUpload: {
                uploadUrl: '/api/member/uploads/editor/image',
              },
              style: {
                definitions: [
                  {name: 'Marker', element: 'span', classes: ['marker'],},
                ]
              },
              heading: {
                options: [
                  {model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph'},
                  {model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1'},
                  {model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2'},
                  {model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3'},
                  {
                    model: 'headingFancy',
                    view: {name: 'p', classes: 'subtitle'},
                    title: t(language, 'Subtitle'),
                    class: 'ck-heading_subtitle',
                    converterPriority: 'high'
                  }
                ]
              },
              toolbar: isMobile ? mobileToolbar : pcToolbar,
              mediaEmbed: {
                previewsInData: true,
                toolbar: ['mediaEmbed']
              },
              list: {properties: {styles: true, startIndex: true}},
              // 기존 커스텀 빌드와 동일한 image toolbar 설정
              image: {
                toolbar: [
                  'imageTextAlternative',
                  'toggleImageCaption',
                  'imageStyle:inline',
                  'imageStyle:block',
                  'imageStyle:side',
                  'linkImage'
                ]
              },
              // 기존 커스텀 빌드와 동일한 table contentToolbar 설정
              table: {
                contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells',
                  // 'toggleTableCaption',
                  //'tableProperties', 'tableCellProperties'
                ]
              },
              link: {
                // 외부링크만 새창으로 열기
                // addTargetToExternalLinks: true,
                // 또는 모든 링크에 적용하려면:
                defaultProtocol: 'https://',
                decorators: {
                  openInNewTab: {
                    mode: 'automatic',
                    callback: () => true,  // 모든 링크에 적용
                    attributes: {
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }
                  }
                }
              },
              // ListIndentFixer 플러그인을 위한 htmlSupport 설정
              // p, li, ul, ol 태그에 margin-left 스타일 허용
              htmlSupport: {
                allow: [
                  {
                    name: 'p',
                    styles: { 'margin-left': true }
                  },
                  {
                    name: 'li',
                    styles: { 'margin-left': true }
                  },
                  {
                    name: 'ul',
                    styles: true
                  },
                  {
                    name: 'ol',
                    styles: true
                  },
                  {
                    name: /^(table|thead|tbody|tfoot|tr|th|td|caption|colgroup|col|figure|figcaption)$/,
                    classes: true,
                    styles: true,
                    attributes: true
                  },
                ]
              }
            } as any}
            data={safeContents || ''}
            onReady={editor => {
              editorInstance.current = editor;
              // toolbar 높이 (1줄당 - 39px)에 따라 editor 동적 크기 조정
              const toolbarElement = editor.ui.view.toolbar.element;
              const toolbarHeight = toolbarElement?.offsetHeight || 39;

              const heightNum = parseInt(props.height, 10);
              const editableInlineHeight = heightNum - toolbarHeight + 39;

              editor.editing.view.change((writer) => {
                const root = editor.editing.view.document.getRoot();
                writer.setStyle("height", `${editableInlineHeight}px`, root);
              });
              // Sticky toolbar 비활성화
              const view = editor.ui.view as any;
              if (view.stickyPanel) {
                view.stickyPanel.limiterElement = null;
              }

              // 툴바목록.
              // console.log("toolbars", Array.from(editor.ui.componentFactory.names()));
              // console.log("plugins", editor.plugins);
              //console.log("plugins json", JSON.stringify(editor.plugins));
            }}
            onChange={(event, editor) => {
              const data = editor.data;
              const content = data.get();
              lastSentContent.current = content;
              props.onChangeData(content);
              //console.log({event, editor, data}, data.get());
            }}
            onBlur={(event, editor) => {
              //console.log('Blur.', editor);
            }}
            onFocus={(event, editor) => {
              //console.log('Focus.', editor);
            }}
          />
        </EditorWrap>
      </Container>
    </React.Suspense>

  );
}

const Container = styled.div<{ $editorHeight: string }>`
  ${EditorStyle};

  .ck-source-editing-area > textarea {
    height: ${p => p.$editorHeight};
    overflow: auto;
    //border: 1px solid #ccc;
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    border-radius: 0;
    border-top: 1px solid rgb(199, 214, 234);
    border-right: none;
    border-bottom: none;
    border-left: none;
    box-shadow: none;
  }

  .SsqEditor.border .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    border-radius: 0;
    border: 1px solid rgb(199, 214, 234);
    box-shadow: none;
  }

  .ck.ck-toolbar.ck-toolbar_grouping {
    border-top: 1px solid rgb(199, 214, 234);
    border-right: none;
    border-left: none;
  }

  .SsqEditor.border .ck.ck-toolbar.ck-toolbar_grouping {
    border-top: 1px solid rgb(199, 214, 234);
    border-right: 1px solid rgb(199, 214, 234);
    border-left: 1px solid rgb(199, 214, 234);
  }

  .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar {
    border-radius: 0;
  }

  .ck-source-editing-area textarea:not([readonly]),
  .ck-source-editing-area textarea:not([readonly]):focus {
    box-shadow: none;
    border-radius: 0;
    border: none;
    border-top: 1px solid rgb(199, 214, 234)
  }

  .SsqEditor.border .ck-source-editing-area textarea:not([readonly]),
  .SsqEditor.border .ck-source-editing-area textarea:not([readonly]):focus {
    box-shadow: none;
    border-radius: 0;
    border: 1px solid rgb(199, 214, 234)
  }

`;
const EditorWrap = styled.div<{ $editorHeight: string }>`
  height: ${p => p.$editorHeight};

  .ck-source-editing-area {
    min-height: ${p => p.$editorHeight};
  }
`;

export default SsqEditor5;
